'use client';

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  User,
  Lock,
  Sun,
  Moon,
  Users,
  LogOut,
  Trash2,
  ChevronRight,
} from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { GLASS_CARD } from '@/components/ui/glass';

import ChangePasswordDialog from '@/components/pages/dialogs/ChangePasswordDialog';
import DeleteProfileDialog from '@/components/pages/dialogs/DeleteProfileDialog';

import { useUsuario } from '@/components/hooks/useUsuario';
import { useProfiles } from '@/components/hooks/useProfiles';
import { useTheme } from '@/components/hooks/useTheme';
import { logoutUser } from '@/components/utils/logoutUser';

const containerVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { staggerChildren: 0.06 } },
};
const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.35 } },
};

const inputClass =
  'w-full border rounded-xl px-3 py-2 text-sm border-gray-300/70 dark:border-white/10 bg-white/70 dark:bg-black/40 supports-[backdrop-filter]:backdrop-blur-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:border-[#103254] focus:ring-1 focus:ring-[#103254] transition';

const ClientProfile = () => {
  const navigate = useNavigate();
  const { usuario, loadingUser, saving, setUsuario, guardarUsuario } =
    useUsuario();
  const { perfiles, loading: loadingProfiles } = useProfiles();
  const { theme, setTheme } = useTheme();

  const [bajaOpen, setBajaOpen] = useState(false);

  const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setUsuario((prev) => ({ ...prev, [id]: value }));
  };

  const handleSave = async () => {
    await guardarUsuario(usuario);
  };

  const handleLogout = async () => {
    await logoutUser();
    navigate('/');
  };

  if (loadingUser) {
    return (
      <div className="flex justify-center items-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-500 dark:border-gray-400 border-t-transparent dark:border-t-transparent" />
      </div>
    );
  }

  return (
    <motion.div
      className="w-full space-y-6 pb-28 lg:pb-0"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      {/* Header */}
      <motion.div variants={itemVariants} className="flex justify-between">
        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
          <span className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">
            Mi perfil
          </span>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-white/10 transition text-gray-600 dark:text-gray-300"
            title={theme === 'dark' ? 'Modo claro' : 'Modo oscuro'}
          >
            {theme === 'dark' ? (
              <Sun className="w-5 h-5 text-yellow-500" />
            ) : (
              <Moon className="w-5 h-5" />
            )}
          </button>
        </div>
      </motion.div>

      {/* Avatar + Nombre */}
      <Card className={`rounded-2xl ${GLASS_CARD}`}>
        <CardContent className="p-4 flex items-center gap-4">
          <div className="w-16 h-16 rounded-full flex items-center justify-center bg-[#103254] shrink-0">
            <span className="text-2xl font-bold text-white">
              {usuario.nombre?.charAt(0)?.toUpperCase() || '?'}
            </span>
          </div>
          <div className="min-w-0">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white truncate">
              {usuario.nombre}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
              {usuario.email}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Datos personales */}
      <Card className={`rounded-2xl ${GLASS_CARD}`}>
        <CardContent className="p-6 space-y-6">
          <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400 mb-2">
            <User className="w-5 h-5" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Datos personales
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label className="text-gray-700 dark:text-gray-300">
                Nombre
              </Label>
              <Input
                id="nombre"
                value={usuario.nombre}
                onChange={handleInput}
                placeholder="Introduce tu nombre"
                className={inputClass}
              />
            </div>

            <div>
              <Label className="text-gray-700 dark:text-gray-300">
                Correo
              </Label>
              <Input
                id="email"
                type="email"
                value={usuario.email}
                disabled
                className={`${inputClass} disabled:bg-gray-100 dark:disabled:bg-white/5`}
              />
            </div>

            <div>
              <Label className="text-gray-700 dark:text-gray-300">
                Teléfono
              </Label>
              <Input
                id="telefono"
                value={usuario.telefono}
                onChange={handleInput}
                placeholder="600000000"
                className={inputClass}
              />
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="w-full sm:w-auto bg-[#103254] hover:bg-[#0d2742] dark:bg-[#103254] dark:hover:bg-[#0d2742] text-white rounded-xl px-5 py-2 transition-colors disabled:opacity-50"
            >
              {saving ? 'Guardando...' : 'Guardar cambios'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Seguridad */}
      <Card className={`rounded-2xl ${GLASS_CARD}`}>
        <CardContent className="p-6 space-y-6">
          <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400 mb-2">
            <Lock className="w-5 h-5" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Seguridad
            </h3>
          </div>
          <ChangePasswordDialog />
        </CardContent>
      </Card>

      {/* Cuenta */}
      <Card className={`rounded-2xl ${GLASS_CARD}`}>
        <CardContent className="p-0 divide-y divide-gray-100 dark:divide-white/10">
          {!loadingProfiles && perfiles.length > 1 && (
            <button
              onClick={() => navigate('/profiles')}
              className="w-full flex items-center gap-3 px-6 py-4 text-left hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
            >
              <div className="w-9 h-9 rounded-full bg-[#103254]/10 dark:bg-blue-400/10 flex items-center justify-center shrink-0">
                <Users className="w-4.5 h-4.5 text-[#103254] dark:text-blue-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  Cambiar de perfil
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {perfiles.length} perfiles disponibles
                </p>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-300 dark:text-gray-600 shrink-0" />
            </button>
          )}

          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-6 py-4 text-left hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors"
          >
            <div className="w-9 h-9 rounded-full bg-red-500/10 flex items-center justify-center shrink-0">
              <LogOut className="w-4.5 h-4.5 text-red-500 dark:text-red-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-red-600 dark:text-red-400">
                Cerrar sesión
              </p>
            </div>
            <ChevronRight className="w-4 h-4 text-red-200 dark:text-red-800 shrink-0" />
          </button>

          {/* Baja del perfil — última opción de la tarjeta, por ser la más
              destructiva de las tres. */}
          <button
            onClick={() => setBajaOpen(true)}
            className="w-full flex items-center gap-3 px-6 py-4 text-left hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors"
          >
            <div className="w-9 h-9 rounded-full bg-red-500/10 flex items-center justify-center shrink-0">
              <Trash2 className="w-4.5 h-4.5 text-red-500 dark:text-red-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-red-600 dark:text-red-400">
                Dar de baja mi perfil
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Podrás reactivarlo cuando quieras
              </p>
            </div>
            <ChevronRight className="w-4 h-4 text-red-200 dark:text-red-800 shrink-0" />
          </button>
        </CardContent>
      </Card>

      <DeleteProfileDialog
        open={bajaOpen}
        onOpenChange={setBajaOpen}
        totalPerfiles={perfiles.length}
      />
    </motion.div>
  );
};

export default ClientProfile;